import { setInterval, clearInterval } from '../tools/timer';
import { dateNow, ONE_SECOND } from '../tools/utils/timeUtils';
import { Observable } from '../tools/observable';
import { mockable } from '../tools/mockable';
import { addTelemetryDebug } from '../domain/telemetry';
import { addEventListener } from './addEventListener';
import { getCookies, setCookie } from './cookie';
export function createCookieAccess(cookieName, configuration, cookieOptions) {
    const cookieStore = mockable(window.cookieStore);
    if (cookieStore) {
        return createCookieStoreAccess(cookieName, configuration, cookieOptions, cookieStore);
    }
    return createDocumentCookieAccess(cookieName, cookieOptions);
}
function createCookieStoreAccess(cookieName, configuration, cookieOptions, cookieStore) {
    const observable = new Observable(() => {
        const listener = addEventListener(configuration, cookieStore, "change" /* DOM_EVENT.CHANGE */, (event) => {
            // Based on our experimentation, we're assuming that entries for the same cookie cannot be in both the 'changed' and 'deleted' arrays.
            // However, due to ambiguity in the specification, we asked for clarification: https://github.com/WICG/cookie-store/issues/226
            const changeEvent = event.changed.some((event) => event.name === cookieName) ||
                event.deleted.some((event) => event.name === cookieName);
            if (changeEvent) {
                observable.notify();
            }
        });
        return listener.stop;
    });
    return {
        async getAll() {
            const items = await cookieStore.getAll(cookieName);
            return items.map((item) => item.value);
        },
        async getAllAndSet(cb) {
            const items = await cookieStore.getAll(cookieName);
            const currentValues = items.map((item) => item.value);
            const { value, expireDelay } = cb(currentValues);
            try {
                await cookieStore.set({
                    name: cookieName,
                    value,
                    expires: dateNow() + expireDelay,
                    path: '/',
                    sameSite: cookieOptions.crossSite ? 'none' : 'strict',
                    domain: cookieOptions.domain,
                    secure: cookieOptions.secure,
                    partitioned: cookieOptions.partitioned,
                });
            }
            catch (error) {
                const documentCookies = getCookies(cookieName);
                // monitor-until: 2026-07-01
                addTelemetryDebug('Failed to set cookie using Cookie Store API', {
                    'error.message': error.message,
                    newValue: value,
                    cookieOptions: {
                        ...cookieOptions,
                    },
                    cookies: items.map((item) => ({
                        ...item,
                    })),
                    cookieCount: items.length,
                    documentCookies,
                });
            }
        },
        observable,
    };
}
export const WATCH_COOKIE_INTERVAL_DELAY = ONE_SECOND;
function createDocumentCookieAccess(cookieName, cookieOptions) {
    let previousCookieValues = getCookies(cookieName);
    const observable = new Observable(() => {
        const watchCookieIntervalId = setInterval(() => {
            const cookieValues = getCookies(cookieName);
            notifyCookieValueIfChanged(cookieValues);
        }, WATCH_COOKIE_INTERVAL_DELAY);
        return () => {
            clearInterval(watchCookieIntervalId);
        };
    });
    function notifyCookieValueIfChanged(cookieValues) {
        if (String(cookieValues) !== String(previousCookieValues)) {
            previousCookieValues = cookieValues;
            observable.notify();
        }
    }
    return {
        getAll() {
            return Promise.resolve(getCookies(cookieName));
        },
        async getAllAndSet(cb) {
            const currentValue = getCookies(cookieName);
            const { value, expireDelay } = cb(currentValue);
            setCookie(cookieName, value, expireDelay, cookieOptions);
            await Promise.resolve();
            notifyCookieValueIfChanged([value]);
        },
        observable,
    };
}
//# sourceMappingURL=cookieAccess.js.map